import { Loader2, Mic, Paperclip, SendHorizontal, Square, X } from "lucide-react";
import * as React from "react";
import { useTranslation } from "react-i18next";

import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { useMicRecorder } from "@/hooks/useMicRecorder";
import { cn } from "@/lib/utils";
import { useChatStore } from "@/stores/chat";

import { LLMSelector } from "./LLMSelector";

interface ComposerProps {
  onSubmit?: (text: string) => void;
  disabled?: boolean;
  placeholder?: string;
}

const MAX_HEIGHT_PX = 6 * 24; // 6 lines

function fmtElapsed(sec: number): string {
  const m = Math.floor(sec / 60);
  const s = sec % 60;
  return `${m}:${s.toString().padStart(2, "0")}`;
}

export function Composer({
  onSubmit,
  disabled = false,
  placeholder,
}: ComposerProps): JSX.Element {
  const { t } = useTranslation();
  const [value, setValue] = React.useState("");
  const taRef = React.useRef<HTMLTextAreaElement>(null);
  const composerDraft = useChatStore((s) => s.composerDraft);
  const setComposerDraft = useChatStore((s) => s.setComposerDraft);

  // Voice recorder (P3-S1)
  const mic = useMicRecorder({
    language: "vi",
    maxDurationSec: 60,
    onTranscribed: (r) => {
      // Append text vào value (preserve nếu user đang gõ dở)
      setValue((prev) => {
        const sep = prev.trim().length > 0 ? " " : "";
        return prev + sep + r.text;
      });
      window.setTimeout(() => taRef.current?.focus(), 0);
    },
  });

  // Consume draft từ store (P2.1-S6 SuggestedPrompts → set draft → composer fills)
  React.useEffect(() => {
    if (composerDraft && composerDraft !== value) {
      setValue(composerDraft);
      setComposerDraft("");
      // Defer focus tới sau khi value applied
      window.setTimeout(() => taRef.current?.focus(), 0);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [composerDraft]);

  // Auto-resize
  React.useEffect(() => {
    const ta = taRef.current;
    if (!ta) return;
    ta.style.height = "auto";
    ta.style.height = `${Math.min(ta.scrollHeight, MAX_HEIGHT_PX)}px`;
  }, [value]);

  const submit = (): void => {
    const trimmed = value.trim();
    if (!trimmed || disabled) return;
    onSubmit?.(trimmed);
    setValue("");
  };

  const handleKeyDown = (e: React.KeyboardEvent<HTMLTextAreaElement>): void => {
    if (e.key === "Enter" && !e.shiftKey && !e.nativeEvent.isComposing) {
      e.preventDefault();
      submit();
    }
    if (e.key === "Escape") {
      setValue("");
    }
  };

  const isRecording = mic.state === "recording";
  const isUploading = mic.state === "uploading";
  const isRequesting = mic.state === "requesting";
  const isMicError = mic.state === "error";
  const micBusy = isRecording || isUploading || isRequesting;
  const composerDisabled = disabled || micBusy;

  // Mic button click handler — toggle record/stop
  const handleMicClick = (): void => {
    if (mic.state === "idle" || mic.state === "error") {
      void mic.start();
    } else if (mic.state === "recording") {
      mic.stop();
    }
  };

  return (
    <div className="border-t bg-background p-3">
      <div
        className={cn(
          "max-w-3xl mx-auto rounded-lg border bg-muted/30 transition-colors",
          "focus-within:border-ring focus-within:bg-background",
          composerDisabled && "opacity-60",
          isRecording && "border-red-500 bg-red-50/40 dark:bg-red-950/20",
        )}
      >
        {/* Recording overlay banner */}
        {isRecording ? (
          <div className="flex items-center gap-2 px-3 py-1.5 text-sm">
            <span className="size-2 rounded-full bg-red-500 animate-pulse" />
            <span className="font-medium text-red-600 dark:text-red-400">
              Đang ghi âm…
            </span>
            <span className="text-muted-foreground tabular-nums">
              {fmtElapsed(mic.elapsedSec)}
            </span>
            <span className="ml-auto text-xs text-muted-foreground">
              Click{" "}
              <Square className="inline size-3" /> để dừng,{" "}
              <X className="inline size-3" /> để hủy
            </span>
          </div>
        ) : null}

        {isUploading ? (
          <div className="flex items-center gap-2 px-3 py-1.5 text-sm">
            <Loader2 className="size-4 animate-spin text-primary" />
            <span className="text-muted-foreground">
              Đang chuyển giọng nói thành văn bản…
            </span>
          </div>
        ) : null}

        {isMicError && mic.errorMessage ? (
          <div className="flex items-center gap-2 px-3 py-1.5 text-sm">
            <span className="text-red-600 dark:text-red-400">
              {mic.errorMessage}
            </span>
            <button
              type="button"
              className="ml-auto text-xs text-muted-foreground underline"
              onClick={mic.reset}
            >
              Đóng
            </button>
          </div>
        ) : null}

        <div className="flex items-end gap-1 px-2 py-1.5">
          <Button
            variant="ghost"
            size="icon"
            disabled={composerDisabled}
            aria-label="Đính kèm"
            className="size-8 shrink-0"
          >
            <Paperclip className="size-4" />
          </Button>
          <Textarea
            ref={taRef}
            value={value}
            onChange={(e) => setValue(e.target.value)}
            onKeyDown={handleKeyDown}
            disabled={composerDisabled}
            placeholder={
              isRecording
                ? "Đang ghi âm — text sẽ điền sau khi dừng"
                : isUploading
                  ? "Đang xử lý giọng nói…"
                  : (placeholder ?? t("chat.composer_placeholder"))
            }
            rows={1}
            className="flex-1 border-0 bg-transparent shadow-none focus-visible:ring-0 px-2 min-h-[36px] resize-none"
          />

          {/* Cancel button — visible khi đang record */}
          {isRecording ? (
            <Button
              variant="ghost"
              size="icon"
              aria-label="Hủy ghi âm"
              className="size-8 shrink-0"
              onClick={mic.cancel}
              type="button"
            >
              <X className="size-4" />
            </Button>
          ) : null}

          {/* Mic button — toggle record / stop. Disabled khi uploading. */}
          <Button
            variant={isRecording ? "default" : "ghost"}
            size="icon"
            disabled={disabled || isUploading || isRequesting}
            aria-label={
              isRecording
                ? "Dừng ghi âm"
                : isRequesting
                  ? "Đang xin quyền mic…"
                  : "Bắt đầu ghi âm"
            }
            className={cn(
              "size-8 shrink-0",
              isRecording && "bg-red-500 hover:bg-red-600 text-white",
            )}
            onClick={handleMicClick}
            type="button"
          >
            {isUploading || isRequesting ? (
              <Loader2 className="size-4 animate-spin" />
            ) : isRecording ? (
              <Square className="size-4 fill-white" />
            ) : (
              <Mic className="size-4" />
            )}
          </Button>

          <Button
            size="icon"
            disabled={composerDisabled || value.trim().length === 0}
            aria-label="Gửi"
            className="size-8 shrink-0"
            onClick={submit}
          >
            <SendHorizontal className="size-4" />
          </Button>
        </div>
        <div className="flex items-center justify-between border-t px-2 py-1">
          <LLMSelector />
          <p className="text-[10px] text-muted-foreground">
            Enter để gửi · Shift+Enter xuống dòng
          </p>
        </div>
      </div>
    </div>
  );
}
